// context/PostContext.js
import React, { createContext, useContext, useState, useMemo, useCallback, useRef } from 'react';

// Create a context for post viewing state
const PostContext = createContext();

// Provider component
export const PostProvider = ({ children }) => {
  // State for the post modal
  const [modalState, setModalState] = useState({
    isOpen: false,
    posts: [],
    currentIndex: 0,
    returnPath: null,
  });
  
  // Cache for posts by userId - persists during the session
  const postsCache = useRef(new Map());
  
  // Cache posts for a user
  const cachePosts = useCallback((userId, posts) => {
    postsCache.current.set(userId, {
      posts,
      timestamp: Date.now()
    });
  }, []);
  
  // Get cached posts for a user
  const getCachedPosts = useCallback((userId) => {
    const cached = postsCache.current.get(userId);
    if (!cached) return null;
    
    // Optional: Add cache expiry (e.g., 30 minutes)
    const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes
    if (Date.now() - cached.timestamp > CACHE_DURATION) {
      postsCache.current.delete(userId);
      return null;
    }
    
    return cached.posts;
  }, []);
  
  // Clear cache for a specific user
  const clearUserPostsCache = useCallback((userId) => {
    postsCache.current.delete(userId);
  }, []);
  
  // Clear all cached posts
  const clearAllPostsCache = useCallback(() => {
    postsCache.current.clear();
  }, []);
  
  // Open the post modal
  const openPostModal = useCallback((posts, initialIndex = 0, returnPath = null) => {
    setModalState(prev => {
      // Don't update if nothing changed
      if (prev.isOpen && 
          prev.currentIndex === initialIndex && 
          prev.returnPath === returnPath &&
          JSON.stringify(prev.posts) === JSON.stringify(posts)) {
        return prev;
      }
      
      return {
        isOpen: true,
        posts,
        currentIndex: initialIndex,
        returnPath
      };
    });
  }, []);
  
  // Close the post modal
  const closePostModal = useCallback(() => {
    setModalState(prev => ({
      ...prev,
      isOpen: false,
    }));
  }, []);
  
  // Change the current post
const changePost = useCallback((newIndex) => {
  setModalState(prev => {
    // Don't update if index is out of bounds or hasn't changed
    if (newIndex < 0 || newIndex >= prev.posts.length || prev.currentIndex === newIndex) {
      return prev;
    }
    
    // Log that we're using cached posts to verify caching is working
    console.log(`Showing cached post at index ${newIndex}`);
    
    return {
      ...prev,
      currentIndex: newIndex,
    };
  });
}, []);

  
  // Update a specific post in cache and modal if open
  const updatePost = useCallback((userId, postId, updatedData) => {
    // Update in cache
    const cached = postsCache.current.get(userId);
    if (cached) {
      const updatedPosts = cached.posts.map(post => 
        post.postId === postId ? { ...post, ...updatedData } : post
      );
      cachePosts(userId, updatedPosts);
    }
    
    // Update in modal if open
    setModalState(prev => {
      if (!prev.isOpen) return prev;
      
      const updatedPosts = prev.posts.map(post => 
        post.postId === postId ? { ...post, ...updatedData } : post
      );
      
      return {
        ...prev,
        posts: updatedPosts
      };
    });
  }, [cachePosts]);
  
  // Remove a post from cache and modal
  const removePost = useCallback((userId, postId) => {
    // Remove from cache
    const cached = postsCache.current.get(userId);
    if (cached) {
      const updatedPosts = cached.posts.filter(post => post.postId !== postId);
      cachePosts(userId, updatedPosts);
    }
    
    // Remove from modal if open
    setModalState(prev => {
      if (!prev.isOpen) return prev;
      
      const updatedPosts = prev.posts.filter(post => post.postId !== postId);
      
      // Adjust current index if needed
      let newIndex = prev.currentIndex;
      if (newIndex >= updatedPosts.length && updatedPosts.length > 0) {
        newIndex = updatedPosts.length - 1;
      }
      
      // Close modal if no posts left
      if (updatedPosts.length === 0) {
        return {
          ...prev,
          isOpen: false,
          posts: [],
          currentIndex: 0
        };
      }
      
      return {
        ...prev,
        posts: updatedPosts,
        currentIndex: newIndex
      };
    });
  }, [cachePosts]);
  
  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    modalState,
    openPostModal,
    closePostModal,
    changePost,
    cachePosts,
    getCachedPosts,
    clearUserPostsCache,
    clearAllPostsCache,
    updatePost,
    removePost
  }), [
    modalState, 
    openPostModal, 
    closePostModal, 
    changePost,
    cachePosts,
    getCachedPosts,
    clearUserPostsCache,
    clearAllPostsCache,
    updatePost,
    removePost
  ]);
  
  return (
    <PostContext.Provider value={contextValue}>
      {children}
    </PostContext.Provider>
  );
};

// Custom hook to use the post context
export const usePostContext = () => useContext(PostContext);

export default PostContext;